import React, { createContext, useContext, useState, useEffect } from 'react';
import { getEffectivePrice } from '../utils/formatPrice';
import { getCartData, clearCache } from '../utils/apiClient';
import { isAuthenticated } from '../utils/auth';
import { showToast } from '../components/ToastContainer';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isCartDrawerOpen, setCartDrawerOpen] = useState(false);

  // Get cached cart from localStorage
  const getCachedCart = () => {
    if (typeof localStorage !== 'undefined') {
      try {
        const cached = localStorage.getItem('cart_items');
        if (cached) {
          const items = JSON.parse(cached);
          return items.map((item) => ({
            ...item,
            adId: item.adId ?? item.ad_id,
          }));
        }
      } catch (e) {
        // Ignore cache errors
      }
    }
    return [];
  };

  // Initialize cartItems with cached data
  const [cartItems, setCartItems] = useState(getCachedCart);
  const [isInitialized, setIsInitialized] = useState(false);

  const getAuthToken = () => {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('auth_token');
    }
    return null;
  };

  const fetchCartFromApi = async (useCache = true) => {
    const token = getAuthToken();
    if (!token) return [];
    
    try {
      const result = await getCartData(useCache);
      const items = result.items || [];
      
      // Cache in localStorage for instant loading
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cart_items', JSON.stringify(items));
      }
      
      return items;
    } catch (error) {
      // Silently handle timeout errors - cached data is already available
      // Only log non-timeout errors
      if (!error.message || !error.message.includes('timeout')) {
        console.error('Failed to fetch cart:', error);
      }
      throw error;
    }
  };

  // Initial load - use cache first, then sync in background
  useEffect(() => {
    const init = async () => {
      try {
        const token = getAuthToken();
        if (!token || !isAuthenticated()) {
          setCartItems([]);
          setIsInitialized(true);
          return;
        }
        
        // Load from cache immediately (already set in useState)
        setIsInitialized(true);
        
        // Sync with API in background
        try {
          const items = await fetchCartFromApi(true);
          setCartItems(items.map((item) => ({
            id: item.id,
            adId: item.ad_id,
            ad: item.ad,
            warranty: item.warranty,
            quantity: item.quantity,
          })));
        } catch (error) {
          // Silently handle timeout errors - cached data is already displayed
          // Only log non-timeout errors
          if (!error.message || !error.message.includes('timeout')) {
            console.error('Failed to sync cart from API:', error);
          }
          // Continue with cached data
        }
      } catch (error) {
        console.error('Failed to initialize cart:', error);
        setIsInitialized(true);
      }
    };

    init();
  }, []);

  // Refresh on login/logout
  useEffect(() => {
    const reload = async () => {
      if (!isAuthenticated()) {
        setCartItems([]);
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('cart_items');
        }
        clearCache('GET:/api/cart:');
        return;
      }
      
      // Load from cache immediately
      const cached = getCachedCart();
      if (cached.length > 0) {
        setCartItems(cached);
      }
      
      // Sync with API in background
      try {
        const items = await fetchCartFromApi(false); // Force fresh data on login
        const mappedItems = items.map((item) => ({
          id: item.id,
          adId: item.ad_id,
          ad: item.ad,
          warranty: item.warranty,
          quantity: item.quantity,
        }));
        setCartItems(mappedItems);
      } catch (error) {
        console.error('Failed to refresh cart:', error);
        // Keep cached data if API fails
      }
    };

    const handleLogin = () => reload();
    const handleLogout = () => {
      setCartItems([]);
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('cart_items');
      }
      clearCache('GET:/api/cart:');
    };

    window.addEventListener('user-login', handleLogin);
    window.addEventListener('user-logout', handleLogout);

    return () => {
      window.removeEventListener('user-login', handleLogin);
      window.removeEventListener('user-logout', handleLogout);
    };
  }, []);

  const addToCart = (ad, warranty = '10days', options = {}) => {
    if (!ad?.id) return null;

    const query = new URLSearchParams();
    if (warranty) query.set('warranty', warranty);
    if (options.quantity != null) query.set('quantity', String(Number(options.quantity)));
    const checkoutUrl = `/checkout/${ad.id}${query.toString() ? `?${query.toString()}` : ''}`;

    if (!isAuthenticated()) {
      const authUrl = `/auth?returnTo=${encodeURIComponent(checkoutUrl)}`;
      if (typeof window !== 'undefined') {
        window.location.href = authUrl;
      }
      return null;
    }

    if (typeof window !== 'undefined') {
      window.location.href = checkoutUrl;
    }
    return { id: `${ad.id}_${Date.now()}`, adId: ad.id, ad, warranty, timestamp: Date.now() };
  };

  const removeFromCart = async (itemId) => {
    const token = getAuthToken();
    if (!token) return;
    
    // Optimistic update
    const previousItems = [...cartItems];
    setCartItems(prev => prev.filter(item => item.id !== itemId));
    
    try {
      const response = await fetch('/api/cart', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId }),
      });
      
      if (!response.ok) {
        // If the backend already removed the item (e.g. after a successful purchase),
        // treat 404 as success to avoid UI flicker/rollback.
        if (response.status === 404) {
          // Persist optimistic state to cache and clear API cache
          const optimisticItems = previousItems.filter(item => item.id !== itemId);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('cart_items', JSON.stringify(optimisticItems));
          }
          clearCache('GET:/api/cart:');
          return;
        }

        // Rollback optimistic update for real failures
        setCartItems(previousItems);
        const result = await response.json();
        showToast(result.error || 'Failed to remove item', 'error');
        return;
      }
      
      const result = await response.json();
      const items = result.items || [];
      const mappedItems = items.map((item) => ({
        id: item.id,
        adId: item.ad_id,
        ad: item.ad,
        warranty: item.warranty,
        quantity: item.quantity,
      }));
      
      setCartItems(mappedItems);
      
      // Update cache
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cart_items', JSON.stringify(mappedItems));
      }
      
      // Clear API cache
      clearCache('GET:/api/cart:');
    } catch (error) {
      // Rollback on error
      setCartItems(previousItems);
      showToast('Failed to remove item', 'error');
    }
  };

  const updateCartItem = async (itemId, updates) => {
    const token = getAuthToken();
    if (!token) return;
    
    // Optimistic update
    const previousItems = [...cartItems];
    setCartItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
    
    try {
      const response = await fetch('/api/cart', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ itemId, ...updates }),
      });
      
      if (!response.ok) {
        // Rollback optimistic update
        setCartItems(previousItems);
        const result = await response.json();
        showToast(result.error || 'Failed to update cart item', 'error');
        return;
      }
      
      const result = await response.json();
      const items = result.items || [];
      const mappedItems = items.map((item) => ({
        id: item.id,
        adId: item.ad_id,
        ad: item.ad,
        warranty: item.warranty,
        quantity: item.quantity,
      }));
      
      setCartItems(mappedItems);
      
      // Update cache
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cart_items', JSON.stringify(mappedItems));
      }
      
      // Clear API cache
      clearCache('GET:/api/cart:');
    } catch (error) {
      // Rollback on error
      setCartItems(previousItems);
      showToast('Failed to update cart item', 'error');
    }
  };

  const clearCart = async () => {
    const token = getAuthToken();
    if (!token) {
      setCartItems([]);
      return;
    }
    const response = await fetch('/api/cart', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ clear: true }),
    });
    if (!response.ok) {
      const result = await response.json();
      showToast(result.error || 'Failed to clear cart', 'error');
      return;
    }
    setCartItems([]);
  };

  const getCartCount = () => cartItems.length;

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const isCurrency = item.ad?.service_type === 'currency';
      const qty = isCurrency && item.quantity != null ? Number(item.quantity) : 1;
      const unitPrice = getEffectivePrice(item.ad);
      const price = unitPrice * qty;
      let warrantyPrice = 0;
      
      // Calculate warranty price if applicable
      if (item.warranty && item.warranty !== 'none') {
        // Add warranty pricing logic here if needed
      }
      
      return total + price + warrantyPrice;
    }, 0);
  };

  const refreshCart = async () => {
    try {
      const items = await fetchCartFromApi(false);
      setCartItems(items.map((item) => ({
        id: item.id,
        adId: item.ad_id,
        ad: item.ad,
        warranty: item.warranty,
        quantity: item.quantity,
      })));
    } catch (e) {
      // Ignore refresh errors
    }
  };

  // Listen for payment success events to refresh cart
  useEffect(() => {
    const handlePaymentSuccess = () => {
      setTimeout(() => {
        refreshCart();
      }, 500);
    };

    window.addEventListener('payment-success', handlePaymentSuccess);

    return () => {
      window.removeEventListener('payment-success', handlePaymentSuccess);
    };
  }, []);

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    getCartCount,
    getCartTotal,
    isInitialized,
    isCartDrawerOpen,
    setCartDrawerOpen
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
